import { Button, EnrollmentHelpText } from '@/shared/components'
import { CheckCircle2, FileSignature } from 'lucide-react'
import { StepHeader } from './StepHeader'
import { getSignatureStatusLabel } from '../docusign/signatureStatusLabel'

type SignDocumentsStepProps = {
  onNext: () => void
  onBack: () => void
  currentStep: number
  totalSteps: number
  isSigned: boolean
  isPreparingSignature: boolean
  isCheckingSignature: boolean
  isFinalizingEnrollment?: boolean
  signatureStatus?: string | null
  onOpenSigning: () => void
  errorMessage?: string | null
}

export function SignDocumentsStep({
  onNext,
  onBack,
  currentStep,
  totalSteps,
  isSigned,
  isPreparingSignature,
  isCheckingSignature,
  isFinalizingEnrollment = false,
  signatureStatus,
  onOpenSigning,
  errorMessage
}: SignDocumentsStepProps) {
  const isBusy =
    isPreparingSignature || (isCheckingSignature && !isSigned) || isFinalizingEnrollment
  const normalizedStatus = signatureStatus?.trim().toUpperCase()
  const statusText = isSigned
    ? 'Signed'
    : getSignatureStatusLabel(signatureStatus, 'Ready')
  const detailText = isSigned
    ? 'Your enrollment agreement is signed and ready for final submission.'
    : isCheckingSignature
      ? 'Checking for DocuSign confirmation.'
      : normalizedStatus === 'FINISH_LATER'
        ? 'You left before finishing. Open the agreement again when you are ready to sign.'
        : 'Open the secure signing window and complete your enrollment agreement.'

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">Sign Your Enrollment Documents</h1>
        </div>

        <div className="mx-auto flex max-w-xl flex-col gap-5 pt-6">
          <div
            className="animate-fade-in-up rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
            style={{ animationDelay: '120ms' }}
          >
            <div className="flex items-start gap-4">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${isSigned ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-primary'}`}
              >
                {isSigned ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : (
                  <FileSignature className="h-5 w-5" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">Enrollment Agreement</h2>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${isSigned ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-primary'}`}
                  >
                    {statusText}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-6 text-gray-500">{detailText}</p>
              </div>
            </div>
          </div>

          <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <Button
              type="button"
              onClick={isSigned ? onNext : onOpenSigning}
              loading={isBusy}
              disabled={isBusy}
              fullWidth
              aria-label={isSigned ? 'Continue enrollment' : 'Open secure signing'}
            >
              {isSigned ? (
                <>
                  <CheckCircle2 className="h-5 w-5" />
                  {isFinalizingEnrollment ? 'Submitting Enrollment...' : 'Continue Enrollment'}
                </>
              ) : (
                <>
                  <FileSignature className="h-5 w-5" />
                  Open Secure Signing
                </>
              )}
            </Button>
          </div>

          {errorMessage && (
            <p className="text-center text-sm font-medium text-red-600">{errorMessage}</p>
          )}

          <div className="animate-fade-in-up text-center" style={{ animationDelay: '260ms' }}>
            <EnrollmentHelpText />
          </div>
        </div>
      </div>
    </div>
  )
}
